import DOMPurify from 'dompurify';
import i18nInstance from '../i18n/index.js';
const i18n = i18nInstance as { t: (key: string, options?: Record<string, unknown>) => string };

// Re-export canonical types from shared (avoids duplication)
// These are the structured types used by the CardEditor app
export type {
  CardData,
  CardDataDb,
  CardLink,
  PersonalData,
  ContactData,
  SocialData,
  ThemeData,
  ImagesData,
  PrivacyData,
} from '@vcard/shared';

export type { AppSettings, ThemeColor } from '@vcard/shared';

// ---------------------------------------------------------------------------
// Translation helper (mirrors App.js translateText)
// ---------------------------------------------------------------------------
const buildI18nOptions = (defaultValue: string | undefined, options: Record<string, unknown> = {}) => {
  const next = { ...options };
  if (defaultValue !== undefined) {
    (next as Record<string, unknown>).defaultValue = defaultValue;
  }
  return next;
};

export const translateText = (key: string, defaultValue?: string, options: Record<string, unknown> = {}): string =>
  i18n.t(key, buildI18nOptions(defaultValue, options)) as string;

// ---------------------------------------------------------------------------
// Safe handler guard
// ---------------------------------------------------------------------------
export const withSafeHandler = (
  handler: ((...args: unknown[]) => unknown) | null | undefined,
  fallback: ((...args: unknown[]) => unknown) | null = null,
  actionName = 'action'
) => {
  return (...args: unknown[]) => {
    if (typeof handler === 'function') {
      return handler(...args);
    }
    if (typeof fallback === 'function') {
      return fallback(...args);
    }
    console.warn(`[UI] Missing handler for ${actionName}`);
  };
};

// ---------------------------------------------------------------------------
// Sanitization helpers
// ---------------------------------------------------------------------------
export const sanitizeText = (text: string | null | undefined): string => {
  if (!text) return '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
};

export const sanitizeHTML = (html: string | null | undefined): string => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u'],
    ALLOWED_ATTR: []
  });
};

// ---------------------------------------------------------------------------
// QR storage helpers
// ---------------------------------------------------------------------------
const QR_STORAGE_KEY = 'vcard:lastQrPayload';

export const saveQrPayloadToStorage = (payload: string): void => {
  try {
    if (!payload) return;
    const record = {
      payload,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(QR_STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    console.warn('Failed to save QR payload', e);
  }
};

export const loadQrPayloadFromStorage = (): string | null => {
  try {
    const raw = localStorage.getItem(QR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { payload?: string };
    return parsed?.payload || null;
  } catch (e) {
    console.warn('Failed to load QR payload', e);
    return null;
  }
};

// ---------------------------------------------------------------------------
// Contact data helpers
// ---------------------------------------------------------------------------
export interface PhoneEntry {
  value: string;
  label: string;
}

export interface AddressData {
  street: string;
  postalCode: string;
  city: string;
  country: string;
  display: string;
  routeQuery: string;
  summary: string;
}

const COUNTRY_OPTIONS = [
  { value: 'DE', labelKey: 'common.countries.DE', defaultLabel: 'Germany' },
  { value: 'AT', labelKey: 'common.countries.AT', defaultLabel: 'Austria' },
  { value: 'CH', labelKey: 'common.countries.CH', defaultLabel: 'Switzerland' },
  { value: 'NL', labelKey: 'common.countries.NL', defaultLabel: 'Netherlands' },
  { value: 'BE', labelKey: 'common.countries.BE', defaultLabel: 'Belgium' },
  { value: 'LU', labelKey: 'common.countries.LU', defaultLabel: 'Luxembourg' },
  { value: 'FR', labelKey: 'common.countries.FR', defaultLabel: 'France' },
  { value: 'ES', labelKey: 'common.countries.ES', defaultLabel: 'Spain' },
  { value: 'IT', labelKey: 'common.countries.IT', defaultLabel: 'Italy' },
  { value: 'PL', labelKey: 'common.countries.PL', defaultLabel: 'Poland' },
  { value: 'CZ', labelKey: 'common.countries.CZ', defaultLabel: 'Czech Republic' },
  { value: 'DK', labelKey: 'common.countries.DK', defaultLabel: 'Denmark' },
  { value: 'SE', labelKey: 'common.countries.SE', defaultLabel: 'Sweden' },
  { value: 'NO', labelKey: 'common.countries.NO', defaultLabel: 'Norway' },
  { value: 'FI', labelKey: 'common.countries.FI', defaultLabel: 'Finland' },
  { value: 'GB', labelKey: 'common.countries.GB', defaultLabel: 'United Kingdom' },
  { value: 'IE', labelKey: 'common.countries.IE', defaultLabel: 'Ireland' },
  { value: 'US', labelKey: 'common.countries.US', defaultLabel: 'United States' },
  { value: 'CA', labelKey: 'common.countries.CA', defaultLabel: 'Canada' },
  { value: 'AU', labelKey: 'common.countries.AU', defaultLabel: 'Australia' }
];

const getCountryLabel = (value = ''): string => {
  const normalized = sanitizeText(value || '').trim();
  if (!normalized) return '';
  const match = COUNTRY_OPTIONS.find((option) => {
    const translatedLabel = translateText(option.labelKey, option.defaultLabel);
    return option.value === normalized || translatedLabel === normalized || option.defaultLabel === normalized;
  });
  return match ? translateText(match.labelKey, match.defaultLabel) : normalized;
};

// Import types from shared for use in this file
import type { PersonalData, ContactData } from '@vcard/shared';

export const collectContactPhones = (contact: ContactData = {}): PhoneEntry[] => {
  const seen = new Set<string>();
  return [
    {
      value: contact.phone,
      label: contact.phoneLabel || translateText('common.phoneLabels.mobile', 'Mobil')
    },
    {
      value: contact.phoneSecondary,
      label: contact.phoneSecondaryLabel || translateText('common.phoneLabels.phone', 'Telefon')
    }
  ]
    .map((entry) => ({
      value: sanitizeText(entry.value || '').trim(),
      label: sanitizeText(entry.label || '').trim()
    }))
    .filter((entry) => {
      if (!entry.value) return false;
      if (seen.has(entry.value)) return false;
      seen.add(entry.value);
      return true;
    });
};

import type { AppSettings } from '@vcard/shared';

export const normalizeContactData = (contact: ContactData = {}): Required<ContactData> => {
  const normalized: Required<ContactData> = {
    email: '',
    phone: '',
    phoneLabel: translateText('common.phoneLabels.mobile', 'Mobil'),
    phoneSecondary: '',
    phoneSecondaryLabel: translateText('common.phoneLabels.phone', 'Telefon'),
    address: '',
    addressStreet: '',
    addressPostalCode: '',
    addressCity: '',
    addressCountry: 'DE',
    showRouteButton: true,
    website: '',
    ...contact
  };

  if (!normalized.addressCountry) {
    normalized.addressCountry = 'DE';
  }

  if (!normalized.addressStreet && !normalized.addressPostalCode && !normalized.addressCity && normalized.address) {
    normalized.addressStreet = normalized.address;
  }

  if (typeof normalized.showRouteButton !== 'boolean') {
    normalized.showRouteButton = true;
  }

  return normalized;
};

export const buildAddressData = (contact: ContactData = {}, personal: PersonalData = {}): AddressData => {
  const normalized = normalizeContactData(contact);
  const street = sanitizeText(normalized.addressStreet || '').trim();
  const postalCode = sanitizeText(normalized.addressPostalCode || '').trim();
  const city = sanitizeText(normalized.addressCity || '').trim();
  const country = getCountryLabel(normalized.addressCountry || '');
  const hasStructuredAddress = Boolean(street || postalCode || city);
  const cityLine = [postalCode, city].filter(Boolean).join(' ').trim();

  if (hasStructuredAddress) {
    const displayLines = [street, cityLine, country].filter(Boolean);
    return {
      street,
      postalCode,
      city,
      country,
      display: displayLines.join(', '),
      routeQuery: displayLines.join(', '),
      summary: [city, country].filter(Boolean).join(', ')
    };
  }

  const legacyAddress = sanitizeText(normalized.address || personal.location || '').trim();
  return {
    street: legacyAddress,
    postalCode: '',
    city: '',
    country: '',
    display: legacyAddress,
    routeQuery: legacyAddress,
    summary: sanitizeText(personal.location || '').trim()
  };
};

// ---------------------------------------------------------------------------
// VCard string builder
// ---------------------------------------------------------------------------
export const escapeVCardValue = (value: string, maxLen = 500): string =>
  sanitizeText(value || '')
    .substring(0, maxLen)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,');

export const buildVCardString = ({
  personal = {},
  contact = {},
  cardUrl = ''
}: {
  personal?: PersonalData;
  contact?: ContactData;
  cardUrl?: string;
}): string => {
  const firstName = escapeVCardValue(personal.firstName || '', 40);
  const lastName = escapeVCardValue(personal.lastName || '', 40);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const company = escapeVCardValue(personal.company || '', 80);
  const title = escapeVCardValue(personal.title || '', 120);
  const email = escapeVCardValue(contact.email || '', 120);
  const website = escapeVCardValue(contact.website || '', 200);
  const bio = escapeVCardValue(personal.bio || '', 1000);
  const addressData = buildAddressData(contact, personal);
  const address = escapeVCardValue(addressData.display || '', 300);
  const phones = collectContactPhones(contact).map((entry, index) => ({
    value: escapeVCardValue(entry.value, 50),
    label: escapeVCardValue(
      entry.label || translateText(index === 0 ? 'common.phoneLabels.mobile' : 'common.phoneLabels.phone', index === 0 ? 'Mobil' : 'Telefon'),
      40
    )
  }));

  const lines = ['BEGIN:VCARD', 'VERSION:3.0'];

  if (fullName) {
    lines.push(`FN:${fullName}`);
    lines.push(`N:${lastName};${firstName};;;`);
  }

  if (company) {
    lines.push(`ORG:${company}`);
  }

  if (title) {
    lines.push(`TITLE:${title}`);
  }

  if (address) {
    lines.push(`ADR;TYPE=WORK:;;${escapeVCardValue(addressData.street || addressData.display || '', 120)};${escapeVCardValue(addressData.city || '', 80)};;${escapeVCardValue(addressData.postalCode || '', 20)};${escapeVCardValue(addressData.country || '', 80)}`);
  }

  phones.forEach((phone, index) => {
    const phoneType = index === 0 ? 'CELL' : 'WORK';
    lines.push(`TEL;TYPE=${phoneType};LABEL=${phone.label}:${phone.value}`);
  });

  if (email) {
    lines.push(`EMAIL;TYPE=WORK:${email}`);
  }

  if (website) {
    lines.push(`URL:${website}`);
  } else if (cardUrl) {
    lines.push(`URL:${escapeVCardValue(cardUrl, 250)}`);
  }

  if (bio) {
    lines.push(`NOTE:${bio}`);
  }

  lines.push('END:VCARD');
  return lines.join('\n');
};

// ---------------------------------------------------------------------------
// QR payload builder
// ---------------------------------------------------------------------------
import type { CardData } from '@vcard/shared';

export const buildQrPayload = (shortCode: string | null | undefined, data: CardData): string => {
  const { personal = {}, contact = {} } = data || {};
  const cardUrl =
    typeof window !== 'undefined' && shortCode
      ? `${window.location.origin}/card/${shortCode}`
      : '';
  return buildVCardString({ personal, contact, cardUrl });
};

// ---------------------------------------------------------------------------
// Card data merge helper
// ---------------------------------------------------------------------------
export const getDefaultTemplate = (settings?: AppSettings): CardData => ({
  personal: {
    firstName: translateText('template.defaults.firstName', 'New'),
    lastName: translateText('template.defaults.lastName', 'User'),
    title: translateText('template.defaults.role', 'Role Title'),
    company: settings?.default_organisation || translateText('template.defaults.company', 'My Organisation'),
    bio: translateText('template.defaults.bio', 'Welcome to the team.'),
    location: translateText('template.defaults.location', 'London, UK')
  },
  contact: {
    email: '',
    phone: '',
    phoneLabel: translateText('common.phoneLabels.mobile', 'Mobil'),
    phoneSecondary: '',
    phoneSecondaryLabel: translateText('common.phoneLabels.phone', 'Telefon'),
    address: '',
    showRouteButton: true,
    website: ''
  },
  social: { linkedin: '', twitter: '', instagram: '', github: '' },
  theme: { color: 'indigo', style: 'modern' },
  images: { avatar: null, banner: null },
  links: [],
  privacy: {
    requireInteraction: false,
    clientSideObfuscation: true,
    blockRobots: true
  }
});

export const mergeCardDataWithDefaults = (cardData: CardData = {}, settings?: AppSettings): CardData => {
  const defaultTemplate = getDefaultTemplate(settings);
  const mergedContact = normalizeContactData({
    ...defaultTemplate.contact,
    ...(cardData.contact || {})
  });

  return {
    ...defaultTemplate,
    ...cardData,
    personal: {
      ...defaultTemplate.personal,
      ...(cardData.personal || {})
    },
    contact: mergedContact,
    social: {
      ...defaultTemplate.social,
      ...(cardData.social || {})
    },
    theme: {
      ...defaultTemplate.theme,
      ...(cardData.theme || {})
    },
    images: {
      ...defaultTemplate.images,
      ...(cardData.images || {})
    },
    links: cardData.links || [],
    privacy: {
      ...defaultTemplate.privacy,
      ...(cardData.privacy || {})
    }
  };
};
